import {
  assemblyClientDomainStaging,
  assemblyDashboardHostnameProd,
  assemblyDashboardHostnameStaging,
  copilotClientDomainStaging,
} from 'src/constants/middlewareConsts';
import { STUDIO_INTERNAL_PORTAL_TAB_NAME } from 'src/constants/studioFtux';

const STAGING_CLIENT_DOMAINS = [
  assemblyClientDomainStaging,
  copilotClientDomainStaging,
];
const LOCAL_HOSTNAME = 'localhost';
// Railway PR previews pair portal-{repo}-pr-{n} with dashboard-{repo}-pr-{n}.
const RAILWAY_PREVIEW_DOMAIN = '.up.railway.app';
const RAILWAY_PORTAL_PREFIX = 'portal-';
const RAILWAY_DASHBOARD_PREFIX = 'dashboard-';

const hasOpenerTab = (value: unknown): boolean =>
  typeof value === 'object' &&
  value !== null &&
  'closed' in value &&
  value.closed === false;

/**
 * Switches back to the CRM tab that opened this client preview. Returns false when
 * there's no live tab to switch to: no opener (not launched from the CRM), a closed
 * one, or one the browser can no longer reach by name.
 */
export const switchToInternalPortalTab = (): boolean => {
  if (!hasOpenerTab(window.opener)) {
    return false;
  }
  // opener.focus() alone is a no-op in Chrome (focus-stealing prevention).
  // Re-targeting the Internal Portal tab by the name the CRM page stamped on
  // it switches the browser to that existing tab (empty URL = don't navigate
  // it); focusing the returned handle brings it forward.
  const internalTab = window.open('', STUDIO_INTERNAL_PORTAL_TAB_NAME);
  // If the CRM tab navigated cross-origin after opening this preview, the
  // browser clears its window.name, so the name no longer resolves and the
  // call above spawns a fresh blank tab instead of switching back. The opener
  // is the CRM tab, so when the name still resolves the handle is that same
  // context (reference equality holds even cross-origin — no property read);
  // otherwise close the stray tab so we truly no-op.
  if (internalTab && internalTab !== window.opener) {
    internalTab.close();
    return false;
  }
  internalTab?.focus();
  return internalTab !== null;
};

/**
 * The dashboard for the client portal this tab is on. Client portals live on their
 * own domain, so a staging client domain means the staging dashboard, a Railway
 * preview its paired dashboard, and anything else (including a custom domain) prod;
 * local dev serves both from one host.
 */
export const getDashboardOrigin = ({
  hostname,
  origin,
}: Pick<Location, 'hostname' | 'origin'>): string => {
  if (hostname === LOCAL_HOSTNAME || hostname.endsWith(`.${LOCAL_HOSTNAME}`)) {
    return origin;
  }
  if (
    hostname.endsWith(RAILWAY_PREVIEW_DOMAIN) &&
    hostname.startsWith(RAILWAY_PORTAL_PREFIX)
  ) {
    return `https://${RAILWAY_DASHBOARD_PREFIX}${hostname.slice(RAILWAY_PORTAL_PREFIX.length)}`;
  }
  const isStaging = STAGING_CLIENT_DOMAINS.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
  );
  return `https://${
    isStaging ? assemblyDashboardHostnameStaging : assemblyDashboardHostnameProd
  }`;
};
